/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    ChangeEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import Config from 'Config';
import { useAppSelector } from 'common/stores/redux/hooks';
import { FormElement, FormRow } from 'common/components/form/FormLayout';
import FormTextView from 'common/components/form/FormTextView';
import * as logger from 'common/utils/Logger';
import Alert from 'common/components/Alert';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_STATUS,
    WorkOrderPhotoTypeAPI,
    WorkOrderVideoTypeAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    AcceptedFile,
    Document,
    Photo,
    PhotoApproval,
    PhotoIntegrity,
    PhotoMetadata,
    PhotoTypeRequirement,
    QuotePhotoTypeRequirement,
    VideoTypeRequirement,
} from 'common/types/documentRequirements';

import {
    ImagePreview,
    OtherPreview,
    PDFPreview,
} from 'common/components/FilePreview';
import _ from 'lodash';
import { getSecuredURL } from 'common/services/S3StorageService';
import {
    usePhotoRequirementsData,
    usePhotoUpload,
} from './useRequirementsPhotosUploadedRowMetadata';
import DocumentRequirementsPhotosUploadedRow from './DocumentRequirementsPhotosUploadedRow';
import WorkOrderRequirementsPhotosUploadedRow, {
    isCompleteVideo,
} from './WorkOrderRequirementsPhotosUploadedRow';
import { isVideo } from '../DocumentRequirementsPhotosUploaded';
import { convertSecondsToMinuteSecond } from '../../DocumentVideoUpload';
import { getAllowFileUpload } from '../../../file-upload/FileUploadTypes';
import {hasVideoRequirement} from "../../../../utils/requirements/requirement-utils";

const NoPhotoThumb = styled.div<{ h?: number }>`
    width: 100%;
    height: ${({ h }) => (h ? `${h}px` : '158px')};
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Config.color.mediumLightGray};
    color: ${Config.color.mediumGray};
    font-weight: 700;
    font-size: 14px;
    cursor: default;
    text-align: center;

    &::before {
        content: 'No\\A Photo';
        white-space: pre-line;
    }
`;

const StyledImagePreview = styled(ImagePreview)`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
`;

export interface DocumentRequirementsPhotosUploadedRowContainerProps {
    photos: Photo[];
    photo: Photo;
    photoTypeRequirements: PhotoTypeRequirement[];
    videoTypeRequirements: VideoTypeRequirement[];
    quotePhotoTypeRequirements: QuotePhotoTypeRequirement[];
    photoTypes?: WorkOrderPhotoTypeAPI[];
    videoTypes?: WorkOrderVideoTypeAPI[];
    disabled?: boolean;
    removeAcls?: boolean;
    isEmptyRow?: boolean;
    handleRemovePhotoOnClick: (photoId: number) => Promise<void>;
    handleEditUploadedPhotoRequirement: (
        photoId: number,
        photoTypeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => void;
    handleShowMetaData: (
        metadata: PhotoMetadata,
        position: { left: string; top: string },
        integrity?: PhotoIntegrity
    ) => void;
    handleGetApprovalHistoryPhotoRequirement: (
        workOrderId: number,
        photoId: number
    ) => Promise<Photo[]>;
    handleSaveApprovalPhotoRequirement: (
        photoId: number,
        approval: Partial<PhotoApproval>
    ) => Promise<void>;
    document: Document;
    handleUploadPhotoRequirement?: (
        file: File,
        photoTypeId: string,
        description: string,
        requiresPullAndClean: boolean,
        requirementTypeId: string
    ) => Promise<void>;
    handleReUploadRejectedPhotoRequirement?: (
        photo: Photo,
        file: File,
        photoTypeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => Promise<void>;
    openRecommendationModal: (photoTypeId: string) => void;
    bulkApprovalSelected?: boolean;
    selectPhotoBulkApproval: (photoId: ChangeEvent<HTMLInputElement>) => void;
    cardWidth?: number;
    mediaHeight?: number;
    cardHeight?: number;
}
const DocumentRequirementsPhotosUploadedRowContainer = ({
    photos,
    photo,
    photoTypeRequirements,
    videoTypeRequirements,
    quotePhotoTypeRequirements,
    photoTypes,
    videoTypes,
    disabled,
    removeAcls,
    isEmptyRow = false,
    handleRemovePhotoOnClick,
    handleEditUploadedPhotoRequirement,
    handleShowMetaData,
    handleGetApprovalHistoryPhotoRequirement,
    handleSaveApprovalPhotoRequirement,
    document,
    handleUploadPhotoRequirement,
    handleReUploadRejectedPhotoRequirement,
    openRecommendationModal,
    bulkApprovalSelected,
    selectPhotoBulkApproval,
    cardWidth,
    mediaHeight,
    cardHeight,
    requirementTypeId,
}: DocumentRequirementsPhotosUploadedRowContainerProps) => {
    const { token, isMotiliAccount, isContractorAccount } = useAppSelector(
        state => state.authentication
    );

    const { workOrderRequirementApprovalReasons, lhsRiskTypes } =
        usePhotoRequirementsData();
    const {
        acceptedFiles,
        action,
        setAcceptedFiles,
        setAction,
        handleOnDrop,
        handleCloseUpload,
        photoRef,
    } = usePhotoUpload();

    useEffect(() => {
        photoRef.current = photo;
    }, [photo, photoRef]);

    const [busy, setBusy] = useState(false);
    const [cardBusy, setCardBusy] = useState(false);
    const [showEditPhotoRequirementsModal, setShowEditPhotoRequirementsModal] =
        useState(false);
    const [videoThumbnailUrl, setVideoThumbnailUrl] = useState<string | null>();
    const [videoDuration, setVideoDuration] = useState<string | undefined>();
    const hasFetchedVideoURL = useRef(false);

    const isRejected = (p: Photo) => {
        return p.approvalStatusId === 'REJECT';
    };

    const authenticationData = useMemo(() => {
        const authenticatedUserId = token?.userId;
        const authenticatedUserIsOps = isMotiliAccount;
        const authenticatedUserIsCon = isContractorAccount;

        const photoUploadedByAuthenticatedUser =
            photo && photo.userId === authenticatedUserId;
        const authenticatedUserCanEdit =
            authenticatedUserIsOps ||
            photoUploadedByAuthenticatedUser ||
            authenticatedUserIsCon;

        return {
            authenticatedUserIsOps,
            authenticatedUserIsCon,
            authenticatedUserCanEdit,
        };
    }, [photo, token?.userId, isMotiliAccount, isContractorAccount]);

    const computedIsEmptyRow = useMemo(() => {
        return isEmptyRow || !photo?.requirement?.mimeType;
    }, [isEmptyRow, photo]);

    const _handleRemovePhotoOnClick = (): Promise<void> => {
        setCardBusy(true);
        return handleRemovePhotoOnClick(photo.id)
            .then(() => setCardBusy(false))
            .catch(() => setCardBusy(false));
    };

    const photoRequirementMet = useCallback(
        (p: Photo) => {
            return (
                (photoTypeRequirements.filter(
                    photoTypeRequirement =>
                        photoTypeRequirement.requirement.id ===
                        p.requirement.photoTypeId
                ).length > 0 &&
                    p.requirement.photo) ||
                false
            );
        },
        [photoTypeRequirements]
    );
    const videoRequirementMet = useCallback(
        (p: Photo) => {
            return (
                (videoTypeRequirements.filter(
                        photoTypeRequirement =>
                            photoTypeRequirement.requirement.id ===
                            p.requirement.photoTypeId
                    ).length > 0 &&
                    p.requirement.video) ||
                false
            );
        },
        [videoTypeRequirements]
    );

    const showReUpload = (p: Photo) => {
        const showStatus = [WORK_ORDER_STATUS.IN_PROCESS];
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            showStatus.includes(document.statusId as WORK_ORDER_STATUS) &&
            isRejected(p)
        );
    };

    const handleEditOnClick = () => {
        setShowEditPhotoRequirementsModal(true);
    };

    const handleCloseOnClick = () => {
        setShowEditPhotoRequirementsModal(false);
    };

    const hasAdditionalNote = (p: Photo) => {
        return (
            (photoRequirementMet(p) &&
                hasApprovalStatus(p) &&
                p.approval?.additionalNotes) ||
            false
        );
    };
    const isContractorView = isContractorAccount && !isMotiliAccount;
    const renderAdditionalNote = (p: Photo) => {
        const label = {
            ACCEPT: 'Accepted comments',
            APPROVE: 'Approved comments',
            REJECT: 'Rejected comments',
        }[p.approvalStatusId || ''];
        return (
            <FormRow>
                <FormElement>
                    <FormTextView
                        dataTestId='approval-additional-notes'
                        messageStyle={{ marginBottom: '0px' }}
                        label={label}
                        value={p.approval?.additionalNotes}
                    />
                </FormElement>
            </FormRow>
        );
    };

    useEffect(() => {
        hasFetchedVideoURL.current = false;
        setVideoThumbnailUrl(null);
    }, [photo?.id, photo?.requirement?.thumbnail]);

    useEffect(() => {
        if (
            isCompleteVideo(photo) &&
            !hasFetchedVideoURL.current &&
            _.isNil(videoThumbnailUrl)
        ) {
            hasFetchedVideoURL.current = true;
            (async () => {
                if (_.isNumber(photo.requirement?.video?.duration)) {
                    setVideoDuration(
                        convertSecondsToMinuteSecond(
                            photo.requirement?.video?.duration / 1000
                        )
                    );
                }

                const url = await getSecuredURL(photo.requirement?.thumbnail);
                setVideoThumbnailUrl(url);
            })();
        }
    }, [photo, videoThumbnailUrl]);

    const renderImage = useCallback(() => {
        if (computedIsEmptyRow) {
            return null;
        }

        if (!photo.requirement.url) {
            return <NoPhotoThumb h={mediaHeight} />;
        }
        if (photo.requirement.mimeType === 'application/pdf') {
            return <PDFPreview />;
        }
        if (
            photo.requirement.mimeType === 'image/png' ||
            photo.requirement.mimeType === 'image/jpeg'
        ) {
            return (
                <StyledImagePreview src={photo.requirement.url} alt='preview' />
            );
        }
        if (isVideo(photo)) {
            if (videoThumbnailUrl) {
                return (
                    <StyledImagePreview src={videoThumbnailUrl} alt='preview' />
                );
            }
            return '';
        }
        return <OtherPreview />;
    }, [computedIsEmptyRow, photo, videoThumbnailUrl, mediaHeight]);

    const allowFiles = useMemo(
        () => getAllowFileUpload(document?.client?.enabledVideoRecording,
            hasVideoRequirement(document?.clientRequirements) ||
            hasVideoRequirement(document?.projectRequirements),
            true,
            requirementTypeId),
        [document, requirementTypeId]
    );

    const handleMetadataOnClick = (e: MouseEvent<HTMLDivElement>) => {
        handleShowMetaData(
            photo.photoMetadata,
            {
                left: `${e.pageX}px`,
                top: `${e.pageY}px`,
            },
            photo.photoIntegrity
        );
    };

    const handleReUpload = async (
        description: string,
        requiresPullAndClean: boolean
    ) => {
        try {
            if (!acceptedFiles?.[0]) {
                return;
            }

            if (action === 'upload' && handleUploadPhotoRequirement) {
                await handleUploadPhotoRequirement(
                    acceptedFiles[0],
                    photo.requirement.photoTypeId,
                    description,
                    requiresPullAndClean,
                    requirementTypeId
                );
            }
            if (
                action === 'reupload' &&
                handleReUploadRejectedPhotoRequirement
            ) {
                await handleReUploadRejectedPhotoRequirement(
                    photo,
                    acceptedFiles[0],
                    photo.requirement.photoTypeId,
                    description,
                    requiresPullAndClean
                );
            }
        } catch (error: any) {
            logger.error(error);
            Alert.error(
                action === 'reupload'
                    ? `Sorry, issue re-upload rejected requirement: ${error.message}`
                    : `Sorry, issue upload photo requirement: ${error.message}`
            );
        }
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setAction(undefined);
    };

    if (
        (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER ||
            document.documentTypeId === DOCUMENT_TYPE.QUOTE) &&
        authenticationData.authenticatedUserIsOps
    ) {
        return (
            <WorkOrderRequirementsPhotosUploadedRow
                cardWidth={cardWidth}
                mediaHeight={mediaHeight}
                cardHeight={cardHeight}
                isEmptyRow={computedIsEmptyRow}
                selectPhotoBulkApproval={selectPhotoBulkApproval}
                bulkApprovalSelected={bulkApprovalSelected}
                key={photo.id?.toString()}
                disabled={disabled}
                removeAcls={removeAcls}
                photo={photo}
                busy={cardBusy}
                photos={photos}
                document={document}
                handleSaveApprovalPhotoRequirement={
                    handleSaveApprovalPhotoRequirement
                }
                photoTypes={photoTypes}
                videoTypes={videoTypes}
                quotePhotoTypeRequirements={quotePhotoTypeRequirements}
                openRecommendationModal={openRecommendationModal}
                renderAdditionalNote={renderAdditionalNote}
                hasAdditionalNote={hasAdditionalNote}
                handleEditOnClick={handleEditOnClick}
                authenticationData={authenticationData}
                handleOnDrop={(newAcceptedFiles, rejectedFiles, newAction) =>
                    handleOnDrop(
                        newAcceptedFiles,
                        rejectedFiles,
                        newAction,
                        allowFiles
                    )
                }
                handleMetadataOnClick={handleMetadataOnClick}
                renderImage={renderImage}
                photoRequirementMet={photoRequirementMet}
                videoRequirementMet={videoRequirementMet}
                showReUpload={showReUpload}
                handleReUpload={handleReUpload}
                handleCloseUpload={handleCloseUpload}
                handleGetApprovalHistoryPhotoRequirement={(
                    workOrderId: number,
                    photoId: number
                ) =>
                    handleGetApprovalHistoryPhotoRequirement(
                        workOrderId,
                        photoId
                    )
                }
                photoTypeRequirements={photoTypeRequirements}
                videoTypeRequirements={videoTypeRequirements}
                handleCloseOnClick={handleCloseOnClick}
                handleEditUploadedPhotoRequirement={
                    handleEditUploadedPhotoRequirement
                }
                showEditPhotoRequirementsModal={showEditPhotoRequirementsModal}
                acceptedFiles={acceptedFiles}
                action={action}
                isRejected={isRejected}
                handleRemovePhotoOnClick={_handleRemovePhotoOnClick}
                hasApprovalStatus={hasApprovalStatus}
                videoDuration={videoDuration}
                requirementTypeId={requirementTypeId}
            />
        );
    }

    return (
        <DocumentRequirementsPhotosUploadedRow
            cardWidth={cardWidth}
            mediaHeight={mediaHeight}
            cardHeight={cardHeight}
            isEmptyRow={computedIsEmptyRow}
            photos={photos}
            photo={photo}
            photoTypes={photoTypes}
            videoTypes={videoTypes}
            disabled={disabled || busy}
            removeAcls={removeAcls}
            handleRemovePhotoOnClick={_handleRemovePhotoOnClick}
            handleEditUploadedPhotoRequirement={
                handleEditUploadedPhotoRequirement
            }
            openRecommendationModal={openRecommendationModal}
            photoTypeRequirements={photoTypeRequirements}
            videoTypeRequirements={videoTypeRequirements}
            quotePhotoTypeRequirements={quotePhotoTypeRequirements}
            document={document}
            authenticationData={authenticationData}
            workOrderRequirementApprovalReasons={
                workOrderRequirementApprovalReasons
            }
            lhsRiskTypes={lhsRiskTypes}
            handleEditOnClick={handleEditOnClick}
            handleCloseOnClick={handleCloseOnClick}
            hasAdditionalNote={hasAdditionalNote}
            renderAdditionalNote={renderAdditionalNote}
            renderImage={renderImage}
            handleMetadataOnClick={handleMetadataOnClick}
            photoRequirementMet={photoRequirementMet}
            showReUpload={showReUpload}
            handleReUpload={handleReUpload}
            showEditPhotoRequirementsModal={showEditPhotoRequirementsModal}
            busy={cardBusy}
            setBusy={setBusy}
            acceptedFiles={acceptedFiles}
            action={action}
            handleOnDrop={(newAcceptedFiles, rejectedFiles, newAction) =>
                handleOnDrop(
                    newAcceptedFiles,
                    rejectedFiles,
                    newAction,
                    allowFiles
                )
            }
            handleCloseUpload={handleCloseUpload}
            hasApprovalStatus={hasApprovalStatus}
            isContractorView={isContractorView}
            videoDuration={videoDuration}
            requirementTypeId={requirementTypeId}
        />
    );
};

const cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser = (
    files: AcceptedFile[]
) => {
    try {
        files.forEach(file => {
            if ((file as any).preview) {
                window.URL.revokeObjectURL((file as any).preview);
            }
        });
    } catch (error) {
        logger.error(error);
    }
};

const hasApprovalStatus = (photo: Photo) => {
    return ['APPROVE', 'REJECT', 'ACCEPT'].includes(
        photo.approvalStatusId || ''
    );
};

export default DocumentRequirementsPhotosUploadedRowContainer;
